import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, User, X, ArrowRight, Users, Target, TrendingUp, Sparkles } from 'lucide-react';
import { useAudience, AUDIENCE_TYPES } from '../context/AudienceContext';
import { useABTest } from '../context/ABTestContext';

const AudienceSelectorModal = () => {
  const { t } = useTranslation();
  const { showSelector, selectAudience, closeSelector, hasSelected } = useAudience();
  const { getTestConfig, trackConversion, trackImpression, isLoaded: abTestLoaded } = useABTest();
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);

  // Get A/B test configuration for audience selector
  const selectorConfig = getTestConfig('AUDIENCE_SELECTOR');

  // Track impression when modal is shown
  useEffect(() => {
    if (showSelector && abTestLoaded && !hasTrackedImpression) {
      trackImpression('audience_selector');
      setHasTrackedImpression(true);
    }
  }, [showSelector, abTestLoaded, hasTrackedImpression, trackImpression]);

  // Reset impression tracking when modal closes
  useEffect(() => {
    if (!showSelector) {
      setHasTrackedImpression(false);
    }
  }, [showSelector]);

  const handleSelectAudience = (type) => {
    // Track conversion
    trackConversion('audience_selector', 'selection', { 
      audienceType: type,
      variant: selectorConfig?.variant,
      selectorType: selectorConfig?.type || 'modal'
    });
    selectAudience(type);
  };

  // Always show modal when showSelector is true (regardless of A/B variant)
  // The A/B test for inline/banner only affects INITIAL display on first visit
  // The "Switch View" button should always open the modal
  if (!showSelector) return null;

  const audiences = [
    {
      type: AUDIENCE_TYPES.B2B,
      icon: Building2,
      title: t('audience.b2b.title', 'For Organizations'),
      subtitle: t('audience.b2b.subtitle', 'Transform your leadership team'),
      description: t('audience.b2b.description', 'Enterprise coaching solutions with ROI tracking, team analytics, and scalable programs for your entire organization.'),
      features: [
        t('audience.b2b.feature1', 'Team & Leadership Development'),
        t('audience.b2b.feature2', 'ROI Tracking & Analytics'),
        t('audience.b2b.feature3', 'Custom Enterprise Programs'),
      ],
      cta: t('audience.b2b.cta', 'Explore Corporate Solutions'),
      gradient: 'from-blue-600 to-indigo-700',
      hoverGradient: 'hover:from-blue-700 hover:to-indigo-800',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200 hover:border-blue-400',
    },
    {
      type: AUDIENCE_TYPES.B2C,
      icon: User,
      title: t('audience.b2c.title', 'For Individuals'),
      subtitle: t('audience.b2c.subtitle', 'Accelerate your personal growth'),
      description: t('audience.b2c.description', 'Personalized 1:1 coaching to unlock your potential, build confidence, and achieve your personal and professional goals.'),
      features: [
        t('audience.b2c.feature1', 'Personalized 1:1 Sessions'),
        t('audience.b2c.feature2', 'Flexible Scheduling'),
        t('audience.b2c.feature3', 'Goal-Focused Coaching'),
      ],
      cta: t('audience.b2c.cta', 'Start Your Journey'),
      gradient: 'from-emerald-600 to-teal-700',
      hoverGradient: 'hover:from-emerald-700 hover:to-teal-800',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      borderColor: 'border-emerald-200 hover:border-emerald-400',
    }
  ];

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={hasSelected ? closeSelector : undefined}
      />
      
      {/* Scroll container — centres on big screens, scrolls on mobile */}
      <div className="relative min-h-full flex items-start sm:items-center justify-center p-3 sm:p-4">
        {/* Modal */}
        <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden animate-scaleIn my-3 sm:my-0">
        {/* Close button - only show if user has already selected before */}
        {hasSelected && (
          <button
            onClick={closeSelector}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            data-testid="audience-modal-close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        )}

        {/* Header */}
        <div className="text-center pt-6 sm:pt-10 pb-4 sm:pb-6 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            {t('audience.badge', 'Personalized Experience')}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
            {t('audience.title', "I'm looking for coaching for...")}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-lg mx-auto">
            {t('audience.subtitle', "Select your path to see tailored solutions designed specifically for your needs.")}
          </p>
        </div>

        {/* Options */}
        <div className="p-4 sm:p-6 md:p-10 grid md:grid-cols-2 gap-4 sm:gap-6">
          {audiences.map((aud) => {
            const Icon = aud.icon;
            return (
              <button
                key={aud.type}
                onClick={() => handleSelectAudience(aud.type)}
                className={`
                  group relative p-4 sm:p-6 rounded-xl sm:rounded-2xl border-2 ${aud.borderColor}
                  bg-white hover:bg-gray-50 transition-all duration-300
                  text-left flex flex-col h-full
                  hover:shadow-xl sm:hover:-translate-y-1
                `}
                data-testid={`audience-select-${aud.type}`}
              >
                {/* Icon */}
                <div className={`w-11 h-11 sm:w-14 sm:h-14 ${aud.iconBg} rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4`}>
                  <Icon className={`w-5 h-5 sm:w-7 sm:h-7 ${aud.iconColor}`} />
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{aud.title}</h3>
                <p className={`text-xs sm:text-sm font-medium ${aud.iconColor} mb-2 sm:mb-3`}>{aud.subtitle}</p>
                <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 flex-grow">{aud.description}</p>

                {/* Features */}
                <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                  {aud.features.map((feature, idx) => (
                    <li key={feature} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full ${aud.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <svg className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${aud.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className={`
                  w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl bg-gradient-to-r ${aud.gradient} ${aud.hoverGradient}
                  text-white text-sm sm:text-base font-semibold text-center
                  flex items-center justify-center gap-2
                  transition-all duration-300 group-hover:shadow-lg
                `}>
                  {aud.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="text-center pb-5 sm:pb-8 px-4 sm:px-6">
          <p className="text-xs sm:text-sm text-gray-500">
            {t('audience.switchNote', "You can switch your preference anytime from the menu.")}
          </p>
        </div>
      </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AudienceSelectorModal;
